import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
// O estilo continua no CSS da tela de chaves: as classes da tabela, dos cartões
// de estatística e da paginação são as mesmas, e são globais. Copiá-las para um
// arquivo novo criaria duas cópias para manter em sincronia.
import '../pages/ApiIntegration.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── Onda 66: o log de requisições virou componente próprio ─────────────────
//
// Ele nasceu como a segunda aba da tela de Chaves de API, e por isso herdou a
// trava de superadministrador que existe para a emissão de credenciais. São
// riscos diferentes: emitir uma chave cria um acesso que sobrevive à sessão de
// quem a criou; ler o log só conta o que já aconteceu. Quem escreve o
// integrador precisa do segundo e não do primeiro — sem o log, o desenvolvedor
// depende de alguém com poder de emitir chave para descobrir por que a
// requisição dele deu 401.
//
// O componente mora aqui, e não na página, porque é usado nos dois lugares: na
// tela própria (`/api-request-log`, permissão `api_request_log`, a partir do
// perfil de consulta) e como aba de `/api-integration`, onde o superadmin
// continua vendo chaves e log lado a lado. Uma fonte, dois pontos de entrada —
// duplicar a tabela era garantir que uma das duas cópias envelhecesse.

// Onda 67: as cores saíram daqui para a folha de estilo.
//
// Eram dois objetos de hexadecimais aplicados por `style` inline — invisíveis
// para quem revisa o CSS, imunes a qualquer mudança de paleta e fora do sistema
// de tokens que o resto da plataforma usa. O que fica no componente é a
// classificação; a cor é decisão da folha.
function classeDoStatus(code) {
  if (code < 300) return 'status-ok';
  if (code < 500) return 'status-aviso';
  return 'status-erro';
}

function classeDoMetodo(metodo) {
  return 'metodo-' + String(metodo || '').toLowerCase();
}

/**
 * Tabela do histórico de chamadas recebidas pela API de Integração.
 *
 * @param {string}  token     JWT da sessão.
 * @param {boolean} standalone `true` na tela própria (mostra o cabeçalho da
 *                             página); `false` quando é aba da tela de chaves,
 *                             que já tem cabeçalho.
 */
export default function ApiRequestLog({ token, standalone = false }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ page: 1, limit: 50 });

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);
      const params = new URLSearchParams(filters).toString();
      const { data } = await axios.get(`${API_BASE}/api/api-keys/logs/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(data.data || []);
      setPagination(data.pagination || {});
    } catch (e) {
      // Onda 66: o erro passou a aparecer na tela. Antes ia só para o console, e
      // uma sessão expirada dava o mesmo resultado visual de "nenhuma
      // requisição registrada" — o desenvolvedor concluía que a chamada dele
      // nem chegou.
      console.error(e);
      setLogs([]);
      setErro(
        e.response?.status === 403
          ? 'Seu perfil não tem acesso ao log de requisições.'
          : 'Não foi possível carregar o log. Verifique a conexão e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handlePage = (p) => setFilters(f => ({ ...f, page: p }));

  return (
    <div className="tab-content api-request-log">
      {standalone && (
        <div className="page-header">
          <h1>Log de Requisições</h1>
          <p>
            Histórico das chamadas que o sistema de gestão faz à API de Integração: qual endpoint,
            com que código de resposta, quando e de que endereço. O contrato dos endpoints está em{' '}
            <Link to="/integration-docs">Documentação da Integração</Link>.
          </p>
        </div>
      )}

      <div className="tab-header">
        <div>
          {!standalone && <h2>Log de Requisições</h2>}
          <p className="tab-desc">
            {standalone
              ? 'Somente as chamadas recebidas. Os eventos que a plataforma envia ficam no Outbox.'
              : 'Histórico de todas as chamadas realizadas via API de Integração.'}
          </p>
        </div>
        <button className="btn-secondary" onClick={loadLogs}>↻ Atualizar</button>
      </div>

      <div className="log-stats">
        <div className="stat-card">
          <span className="stat-value">{pagination.total?.toLocaleString('pt-BR') || 0}</span>
          <span className="stat-label">Total de Requisições</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{logs.filter(l => l.status_code < 300).length}</span>
          <span className="stat-label">Sucesso (nesta página)</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{logs.filter(l => l.status_code >= 400).length}</span>
          <span className="stat-label">Erros (nesta página)</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {logs.length ? Math.round(logs.reduce((a, l) => a + (l.duration_ms || 0), 0) / logs.length) : 0} ms
          </span>
          <span className="stat-label">Latência Média</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Carregando logs...</div>
      ) : erro ? (
        <div className="empty-state log-erro">
          <span className="empty-icon">⚠️</span>
          <p>{erro}</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📋</span>
          <p>Nenhuma requisição registrada ainda.</p>
        </div>
      ) : (
        <>
          <div className="keys-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Integração</th>
                  <th>Método</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Latência</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td className="text-nowrap">{new Date(l.created_at).toLocaleString('pt-BR')}</td>
                    <td>
                      <strong>{l.key_name}</strong>
                      <small className="row-desc">{l.key_preview}</small>
                    </td>
                    <td>
                      <span className={`method-badge-sm ${classeDoMetodo(l.method)}`}>
                        {l.method}
                      </span>
                    </td>
                    <td><code className="endpoint-path">{l.endpoint}</code></td>
                    <td>
                      <span className={`status-code ${classeDoStatus(l.status_code)}`}>
                        {l.status_code}
                      </span>
                    </td>
                    <td className="text-right">{l.duration_ms ? `${l.duration_ms} ms` : '—'}</td>
                    <td className="text-muted">{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button disabled={filters.page <= 1} onClick={() => handlePage(filters.page - 1)}>← Anterior</button>
              <span>Página {filters.page} de {pagination.pages} ({pagination.total?.toLocaleString('pt-BR')} registros)</span>
              <button disabled={filters.page >= pagination.pages} onClick={() => handlePage(filters.page + 1)}>Próxima →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
